// ============================================================================
// MapView.tsx — ЕДИНЫЙ переиспользуемый компонент карты (MapLibre GL JS).
//
// Источник тайлов — из lib/geo/mapConfig.ts (настройки/константы):
//   PMTiles (офлайн/контур, `opfs:<имя>` — архив в OPFS) → иначе растровые XYZ.
// Маркеры — DOM-элементы (без symbol-слоёв → не нужны fonts/glyphs).
// Кнопка «Где я» (showLocate) — lib/geolocation.captureLocation → центрирование.
//
// Компонент НЕ знает про объекты базы и чат: это чистый мех-слой карты
// (SSoT/SRP). DB-объекты — GeoObjectsMap, геометка — GeoMessage*.
// ============================================================================

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { createLogger } from '@/lib/logger';
import { buildMapStyle } from '@/lib/geo/mapStyle';
import {
  GEO_DEFAULT_CENTER,
  GEO_DEFAULT_ZOOM,
  resolveGeoTileSource,
  type GeoTileSource,
} from '@/lib/geo/mapConfig';
import { settingsStore } from '@/lib/settingsStore';
import type { GeoPoint } from '@/lib/geolocation';
import { GeoLocateButton } from './GeoLocateButton';

const log = createLogger('geo:map');

export type GeoMarkerKind = 'object' | 'selected' | 'point' | 'self';

export interface GeoMarker {
  id: string;
  lat: number;
  lon: number;
  label: string;
  kind?: GeoMarkerKind;
}

export interface MapViewHandle {
  /** Центрировать карту на точке (и, при желании, изменить масштаб). */
  recenter: (lat: number, lon: number, zoom?: number) => void;
  /** Показать все маркеры (fit bounds). */
  fitToMarkers: () => void;
  /** Доступ к экземпляру MapLibre (для продвинутого использования). */
  getMap: () => maplibregl.Map | null;
}

export interface MapViewProps {
  markers?: GeoMarker[];
  initialCenter?: { lat: number; lon: number };
  initialZoom?: number;
  /** Автоматически показывать все маркеры после загрузки (по умолчанию да). */
  autoFit?: boolean;
  /** Кнопка «Где я» (по умолчанию да). */
  showLocate?: boolean;
  /** Масштаб после определения местоположения. */
  locateZoom?: number;
  /** Кнопки навигации (по умолчанию да). */
  showNavigation?: boolean;
  interactive?: boolean;
  height?: number | string;
  className?: string;
  /** Явный источник тайлов (иначе — из настроек). */
  tileSource?: GeoTileSource;
  onMarkerClick?: (marker: GeoMarker) => void;
  onMapClick?: (lat: number, lon: number) => void;
}

const MARKER_COLORS: Record<GeoMarkerKind, string> = {
  object: 'var(--color-primary)',
  selected: 'var(--color-destructive)',
  point: '#e8a33d',
  self: '#2f80ed',
};

function markerKey(m: GeoMarker): string {
  return `${m.id}:${m.lat.toFixed(6)}:${m.lon.toFixed(6)}:${m.kind || 'object'}`;
}

export const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView(
  {
    markers = [],
    initialCenter,
    initialZoom,
    autoFit = true,
    showLocate = true,
    locateZoom = 14,
    showNavigation = true,
    interactive = true,
    height = 360,
    className,
    tileSource,
    onMarkerClick,
    onMapClick,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerObjsRef = useRef<maplibregl.Marker[]>([]);
  const markersRef = useRef<GeoMarker[]>(markers);
  const onMarkerClickRef = useRef(onMarkerClick);
  const onMapClickRef = useRef(onMapClick);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selfPoint, setSelfPoint] = useState<GeoPoint | null>(null);

  const [resolvedTiles, setResolvedTiles] = useState<GeoTileSource>(
    () => tileSource || resolveGeoTileSource(),
  );

  // Настройки тайлов могут догидратироваться (user_settings) — перечитываем
  // детерминированно по событию хранилища настроек.
  useEffect(() => {
    if (tileSource) {
      setResolvedTiles(tileSource);
      return;
    }
    setResolvedTiles(resolveGeoTileSource());
    return settingsStore.subscribe(() => setResolvedTiles(resolveGeoTileSource()));
  }, [tileSource]);

  onMarkerClickRef.current = onMarkerClick;
  onMapClickRef.current = onMapClick;

  const allMarkers = useMemo<GeoMarker[]>(() => {
    if (!selfPoint) return markers;
    return [...markers, { id: '__self', lat: selfPoint.lat, lon: selfPoint.lon, label: 'Вы здесь', kind: 'self' }];
  }, [markers, selfPoint]);
  markersRef.current = allMarkers;

  const markersKey = useMemo(() => allMarkers.map(markerKey).join('|'), [allMarkers]);
  const styleKey = `${resolvedTiles.kind}:${resolvedTiles.url}:${resolvedTiles.attribution}`;

  const syncMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    for (const m of markerObjsRef.current) m.remove();
    markerObjsRef.current = [];
    for (const m of markersRef.current) {
      const el = document.createElement('div');
      el.style.cssText =
        `width:16px;height:16px;border-radius:50%;` +
        `border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35),0 1px 3px rgba(0,0,0,.35);` +
        `background:${MARKER_COLORS[m.kind || 'object']};cursor:pointer;`;
      el.title = m.label;
      const marker = new maplibregl.Marker({ element: el }).setLngLat([m.lon, m.lat]);
      if (m.label) {
        marker.setPopup(new maplibregl.Popup({ offset: 14, closeButton: false }).setText(m.label));
      }
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const current = markersRef.current.find((x) => x.id === m.id);
        if (current) onMarkerClickRef.current?.(current);
      });
      marker.addTo(map);
      markerObjsRef.current.push(marker);
    }
  }, []);

  const fitToMarkers = useCallback(() => {
    const map = mapRef.current;
    const points = markersRef.current;
    if (!map || points.length === 0) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const p of points) bounds.extend([p.lon, p.lat]);
    map.fitBounds(bounds, { padding: 48, maxZoom: 15, duration: 400 });
  }, []);

  const recenter = useCallback((lat: number, lon: number, zoom?: number) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({ center: [lon, lat], zoom: zoom ?? map.getZoom(), duration: 500 });
  }, []);

  useImperativeHandle(ref, () => ({
    recenter,
    fitToMarkers,
    getMap: () => mapRef.current,
  }), [recenter, fitToMarkers]);

  // Создание карты (пересоздаётся только при смене источника тайлов).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setError(null);
    setReady(false);
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container,
        style: buildMapStyle(resolvedTiles),
        center: initialCenter ? [initialCenter.lon, initialCenter.lat] : GEO_DEFAULT_CENTER,
        zoom: initialZoom ?? GEO_DEFAULT_ZOOM,
        interactive,
        attributionControl: { compact: true },
      });
    } catch (e: any) {
      log.error('Не удалось создать карту', { error: e?.message || String(e) });
      setError(e?.message || 'Не удалось создать карту');
      return;
    }
    mapRef.current = map;
    if (showNavigation) map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('click', (e) => onMapClickRef.current?.(e.lngLat.lat, e.lngLat.lng));
    map.on('error', (e: any) => {
      log.warn('Ошибка карты', { error: e?.error?.message || String(e?.error || '') });
    });
    map.once('load', () => {
      setReady(true);
      syncMarkers();
      if (autoFit && markersRef.current.length > 0) fitToMarkers();
    });
    return () => {
      for (const m of markerObjsRef.current) m.remove();
      markerObjsRef.current = [];
      mapRef.current = null;
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleKey]);

  // Синхронизация маркеров.
  useEffect(() => {
    if (ready) syncMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markersKey, ready, syncMarkers]);

  // Автопоказ маркеров при их смене (когда карта уже готова).
  useEffect(() => {
    if (ready && autoFit && markersRef.current.length > 0) fitToMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markersKey, ready, autoFit, fitToMarkers]);

  const handleLocated = (point: GeoPoint) => {
    setSelfPoint(point);
    recenter(point.lat, point.lon, locateZoom);
  };

  return (
    <div className={className} style={{ position: 'relative', height, width: '100%' }}>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {showLocate && (
        <div style={{ position: 'absolute', right: 10, bottom: 24, zIndex: 2 }}>
          <GeoLocateButton onLocated={handleLocated} />
        </div>
      )}
      {error && (
        <div
          className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1"
          style={{ position: 'absolute', left: 10, top: 10, zIndex: 2, maxWidth: '70%' }}
        >
          {error}
        </div>
      )}
    </div>
  );
});
